import { useAppRouter } from '@my-medication/shared-components/providers/AppRouterProvider';
import { Button } from '@my-medication/shared-components/ui/Button';
import { GeneralKeys } from '@i18n/translations';
import { FunctionComponent, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { recoverFromChunkError } from '@helpers/chunkRecovery';
import { loadPage, reloadPage } from '@helpers/window';
import { reportDatadogError, startSessionReplayRecording } from '@services/datadog';

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && /dynamically imported module|failed to fetch/i.test(error.message);

const toReportableError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  if (isRouteErrorResponse(error)) return new Error(`Route error response: ${error.status} ${error.statusText}`);
  return new Error(String(error));
};

const AppRouteError: FunctionComponent = () => {
  const error = useRouteError();
  const chunkLoadError = isChunkLoadError(error);
  const { t } = useTranslation(['general', 'insuranceModal']);
  const { navigateTo } = useAppRouter();

  const handleNavigateHome = useCallback(() => {
    if (chunkLoadError) {
      loadPage('/');
      return;
    }

    void navigateTo('/');
  }, [chunkLoadError, navigateTo]);

  useEffect(() => {
    startSessionReplayRecording();

    const reportableError = toReportableError(error);
    if (recoverFromChunkError(reportableError, 'route-error')) return;

    reportDatadogError(reportableError, { chunkLoadError: false });
  }, []);

  return (
    <div className="bg-white px-4 py-12">
      <div className="mx-auto flex min-h-128 max-w-360 flex-wrap">
        <div className="flex w-full flex-col items-center gap-4 text-center">
          <h1 className="headline-1 m-0 w-full text-center">{t('general:genericError')}</h1>
          <p className="m-0 w-full text-body-2">{t('insuranceModal:genericError')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button name="reload" onClick={reloadPage} className="h-14.5 max-h-14.5 w-fit flex-1">
              {t('general:reloadPage')}
            </Button>
            <Button name="home" onClick={handleNavigateHome} className="h-14.5 max-h-14.5 w-fit flex-1">
              {t(GeneralKeys.BackToDashboard)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

AppRouteError.displayName = 'AppRouteError';

export { AppRouteError };
